import { Chip, Typography, Box, SvgIcon, useTheme, SxProps, Theme } from '@mui/material';
import { AIPromptIcon } from '@/public/icons/bi_icons';
import { TypographyVariants } from '@/app/styles/themes/typography';

interface CustomChipsProps {
	selected?: boolean;
	label: string;
	onClick?: () => void;
	showIcon?: boolean;
	iconSx?: SxProps<Theme>;
	maxLines?: number; // optional prop to limit lines
	dataTestId?: string; // optional prop for testing
}

const CustomChips = ({
	selected = false,
	label,
	onClick,
	showIcon = false,
	iconSx,
	maxLines = 1, // default to 3 lines
	dataTestId="",
}: CustomChipsProps) => {
	const theme = useTheme();

	const iconNode = showIcon ? (
		<SvgIcon
			component={AIPromptIcon}
			fontSize="small"
			sx={{
				height: { xs: '16px', sm: '20px' },
				width: { xs: '16px', sm: '20px' },
				mt: '2px',
				mr: '6px',
				...iconSx,
			}}
			aria-hidden
		/>
	) : null;

	const renderLabel = (
		<Box
			sx={{
				mt: '1px',
				display: 'inline-flex',
				alignItems: 'center',
				maxWidth: '100%',
			}}
		>
			{iconNode}
			<Typography
				variant={TypographyVariants.smallRegular}
				color={(t) => t.palette.foreground.neutral.onSurfaceVar04}
				sx={{
					whiteSpace: 'normal',         // allow wrapping
					wordBreak: 'break-word',      // break long words
					display: '-webkit-box',
					WebkitLineClamp: maxLines,    // limit to N lines
					WebkitBoxOrient: 'vertical',
					overflow: 'hidden',
				}}
			>
				{label}
			</Typography>
		</Box>
	);

	return (
		<Chip
			onClick={onClick}
			data-testid={dataTestId}
			sx={{
				backgroundColor: selected
					? theme.palette.surface.brand.primaryHighlight
					: 'transparent',
				border: '1px solid',
				borderColor: selected
					? theme.palette.surface.brand.primary
					: theme.palette.stroke.neutral.low,
				'&:hover': {
					backgroundColor: selected
						? theme.palette.surface.brand.primaryHighlight
						: 'transparent',
				},
				maxWidth: 400, // <-- increase the max width
				height: 'auto', // allow vertical growth
				textAlign: 'left',
				whiteSpace: 'normal', // wrap text
				overflow: 'visible',  // prevent clipping
				py: 1,
			}}
			label={renderLabel}
		/>
	);
};

export default CustomChips;
